import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Users, UserPlus, Trash2, KeyRound, Crown, Loader2, ShieldCheck,
  AlertCircle, X, Check, Sparkles, ArrowUpRight
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLAN_LABELS = {
  free: 'Free', basico: 'Básico', pro: 'Pro',
  enterprise: 'Empresarial', empresarial: 'Empresarial', elite: 'Elite', five_stars: '5 Estrelas'
};

// Tabela comparativa: gerentes EXTRAS por plano (alem do proprio dono)
const PLAN_TEAM_TABLE = [
  { id: 'free', label: 'Free', total: 1, extras: 0, price: 'Grátis' },
  { id: 'basico', label: 'Básico', total: 3, extras: 2, price: 'R$ 29,90/mês', recommended: true },
  { id: 'pro', label: 'Pro', total: 5, extras: 4, price: 'R$ 39,90/mês' },
  { id: 'enterprise', label: 'Empresarial', total: 8, extras: 7, price: 'R$ 59,90/mês' },
  { id: 'elite', label: 'Elite', total: 15, extras: 14, price: 'R$ 99,90/mês' },
  { id: 'five_stars', label: '5 Estrelas', total: 999, extras: 'Ilimitado', price: 'R$ 149,90/mês' },
];

const UpgradeNotice = ({ planLabel, limit, currentPlan }) => (
  <div className="p-4 bg-gradient-to-br from-amber-50 to-amber-100/40 border border-amber-200 rounded-xl space-y-3" data-testid="team-upgrade-notice">
    <div className="flex items-start gap-2 text-amber-900 text-sm">
      <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
      <p>
        Seu plano <strong>{planLabel}</strong> permite só <strong>{limit === 999 ? 'ilimitados' : limit}</strong>{' '}
        {limit === 1 ? 'usuário' : 'usuários'}. Para adicionar gerentes e funcionários, escolha um plano abaixo.
      </p>
    </div>

    <div className="bg-white rounded-lg border border-amber-200 overflow-hidden">
      <div className="px-3 py-2 bg-amber-100/50 border-b border-amber-200">
        <p className="text-[11px] font-bold text-amber-900 uppercase tracking-wider flex items-center gap-1.5">
          <Sparkles className="w-3 h-3" /> Quantos gerentes cada plano libera
        </p>
      </div>
      <div className="divide-y divide-slate-100">
        {PLAN_TEAM_TABLE.map(p => {
          const isCurrent = p.id === currentPlan;
          return (
            <div
              key={p.id}
              className={`flex items-center justify-between gap-2 px-3 py-2.5 text-xs ${
                isCurrent ? 'bg-slate-50' : p.recommended ? 'bg-emerald-50/60' : 'hover:bg-slate-50/50'
              }`}
              data-testid={`plan-row-${p.id}`}
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`font-bold ${isCurrent ? 'text-slate-500' : 'text-[#0A2647]'}`}>{p.label}</span>
                  {isCurrent && (
                    <span className="text-[9px] px-1.5 py-0.5 bg-slate-200 text-slate-600 rounded-full uppercase tracking-wider font-bold">
                      Atual
                    </span>
                  )}
                  {p.recommended && !isCurrent && (
                    <span className="text-[9px] px-1.5 py-0.5 bg-emerald-600 text-white rounded-full uppercase tracking-wider font-bold">
                      Mais indicado para começar
                    </span>
                  )}
                  <span className="text-slate-500">{p.price}</span>
                </div>
                <span className={`font-semibold ${isCurrent ? 'text-slate-500' : 'text-emerald-600'}`}>
                  {p.extras === 0 ? '— sem gerentes' : p.extras === 'Ilimitado' ? 'Gerentes ilimitados' : `+${p.extras} gerente(s)`}
                </span>
              </div>
              {!isCurrent && p.id !== 'free' && (
                <Link to={`/plans?plan=${p.id}`} className="flex-shrink-0">
                  <Button
                    size="sm"
                    className={`h-8 px-3 text-xs font-semibold ${p.recommended ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-[#0A2647] hover:bg-[#0e3260]'} text-white`}
                    data-testid={`choose-plan-${p.id}`}
                  >
                    Escolher
                  </Button>
                </Link>
              )}
            </div>
          );
        })}
      </div>
    </div>

    <Link to="/plans" className="block">
      <Button className="w-full bg-[#C5A059] hover:bg-[#b08e4a] text-white gap-2 h-10 font-semibold" data-testid="upgrade-plan-btn">
        <ArrowUpRight className="w-4 h-4" />
        Ver todos os planos
      </Button>
    </Link>
  </div>
);

export const SettingsTeamTab = () => {
  const [team, setTeam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [resetTarget, setResetTarget] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  const fetchTeam = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/company/team`, { headers: { Authorization: `Bearer ${token}` } });
      setTeam(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao carregar equipe');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchTeam(); }, [fetchTeam]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || form.password.length < 6) {
      toast.error('Preencha todos os campos. Senha precisa ter ao menos 6 caracteres.');
      return;
    }
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/company/team`, form, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`${form.name} adicionado à equipe!`);
      setForm({ name: '', email: '', password: '' });
      setShowForm(false);
      fetchTeam();
    } catch (e) {
      const msg = e.response?.data?.detail || 'Erro ao adicionar';
      if (String(msg).toLowerCase().includes('limite')) {
        setShowForm(false);
        setShowUpgradeModal(true);
      } else {
        toast.error(msg);
      }
    } finally { setSubmitting(false); }
  };

  const handleRemove = async (member) => {
    if (!window.confirm(`Remover ${member.name} da equipe?\n\nEle perderá o acesso ao painel imediatamente.`)) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/company/team/${member.id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`${member.name} removido`);
      fetchTeam();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao remover');
    }
  };

  const handleResetPassword = async () => {
    if (newPassword.length < 6) {
      toast.error('Senha precisa ter ao menos 6 caracteres');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/company/team/${resetTarget.id}/reset-password`, {
        new_password: newPassword
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Senha de ${resetTarget.name} redefinida`);
      setResetTarget(null);
      setNewPassword('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao redefinir');
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[#0A2647]" /></div>;
  }

  const planLabel = PLAN_LABELS[team?.plan] || team?.plan || 'Free';
  const canAddMore = team?.can_add_more;
  const limitReached = !canAddMore;

  return (
    <div className="space-y-6" data-testid="team-tab">
      <Card className="card-premium">
        <CardHeader className="bg-gradient-to-r from-[#0A2647] to-[#0D3158] text-white rounded-t-xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center">
              <Users className="w-5 h-5" />
            </div>
            <div>
              <CardTitle className="text-base font-semibold">Equipe da Empresa</CardTitle>
              <CardDescription className="text-slate-300 text-sm">
                Adicione gerentes e funcionários para acessar o painel
              </CardDescription>
            </div>
          </div>
        </CardHeader>

        <CardContent className="pt-6 space-y-4">
          {/* Resumo do plano */}
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-200">
            <div>
              <p className="text-xs text-slate-500 font-medium uppercase tracking-wider">Plano Atual</p>
              <p className="text-lg font-bold text-[#0A2647]">
                {planLabel}
                {team?.limit === 1 && (
                  <span className="ml-2 text-xs font-medium text-slate-500">· Apenas o dono</span>
                )}
              </p>
            </div>
            <div className="text-right">
              <p className="text-xs text-slate-500 font-medium uppercase tracking-wider">Usuários</p>
              <p className={`text-lg font-bold ${limitReached ? 'text-amber-600' : 'text-emerald-600'}`}>
                {team?.used} / {team?.limit === 999 ? '∞' : team?.limit}
              </p>
            </div>
          </div>

          {limitReached && (
            <UpgradeNotice planLabel={planLabel} limit={team?.limit} currentPlan={team?.plan} />
          )}

          {/* Lista de membros */}
          <div className="space-y-2">
            {team?.members?.map(m => (
              <div key={m.id} className="flex items-center justify-between p-3 bg-white border border-slate-200 rounded-xl hover:shadow-sm transition" data-testid={`team-member-${m.id}`}>
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                    m.is_owner ? 'bg-[#C5A059]/20 text-[#C5A059]' : 'bg-slate-100 text-slate-500'
                  }`}>
                    {m.is_owner ? <Crown className="w-5 h-5" /> : <ShieldCheck className="w-5 h-5" />}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-slate-900 truncate flex items-center gap-2">
                      {m.name}
                      {m.is_owner && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#C5A059]/15 text-[#C5A059] text-[10px] font-bold uppercase tracking-wider">
                          Dono
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-slate-500 truncate">{m.email}</p>
                  </div>
                </div>

                {!m.is_owner && (
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={() => { setResetTarget(m); setNewPassword(''); }}
                      className="p-2 hover:bg-blue-50 rounded-lg text-blue-600 transition"
                      title="Redefinir senha"
                      data-testid={`reset-password-${m.id}`}
                    >
                      <KeyRound className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleRemove(m)}
                      className="p-2 hover:bg-rose-50 rounded-lg text-rose-600 transition"
                      title="Remover do time"
                      data-testid={`remove-member-${m.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Form para adicionar */}
          {showForm ? (
            <form onSubmit={handleAdd} className="bg-slate-50 border-2 border-[#0A2647]/20 rounded-xl p-4 space-y-3" data-testid="add-member-form">
              <div className="flex items-center justify-between mb-1">
                <p className="text-sm font-bold text-[#0A2647]">Adicionar novo membro</p>
                <button type="button" onClick={() => setShowForm(false)} className="p-1 hover:bg-slate-200 rounded"><X className="w-4 h-4" /></button>
              </div>
              <div>
                <Label className="text-xs">Nome do gerente</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ex: João Silva" className="mt-1" data-testid="member-name" />
              </div>
              <div>
                <Label className="text-xs">Email (usado para login)</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="joao@empresa.com" className="mt-1" data-testid="member-email" />
              </div>
              <div>
                <Label className="text-xs">Senha inicial (mín. 6 caracteres)</Label>
                <Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Pelo menos 6 caracteres" className="mt-1" data-testid="member-password" />
                <p className="text-[10px] text-slate-500 mt-1">💡 Envie essa senha ao seu gerente. Ele pode trocar depois.</p>
              </div>
              <Button type="submit" disabled={submitting} className="w-full bg-[#0A2647] hover:bg-[#0e3260] text-white gap-2" data-testid="confirm-add-member">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                Adicionar à equipe
              </Button>
            </form>
          ) : (
            <Button
              onClick={() => (canAddMore ? setShowForm(true) : setShowUpgradeModal(true))}
              className={`w-full gap-2 h-12 text-white ${canAddMore ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-[#0A2647] hover:bg-[#0e3260]'}`}
              data-testid="add-member-btn"
            >
              <UserPlus className="w-4 h-4" />
              Adicionar novo membro à equipe
            </Button>
          )}

          {/* Microcopy */}
          <div className="text-[11px] text-slate-500 leading-relaxed pt-2 border-t border-slate-100">
            <p><strong>O que cada usuário pode fazer:</strong> ver avaliações, gerenciar marca, acessar relatórios.</p>
            <p className="mt-1"><strong>Só o dono</strong> pode adicionar/remover membros e mudar o plano.</p>
          </div>
        </CardContent>
      </Card>

      {/* UPGRADE MODAL (tentou adicionar sem vaga) */}
      {showUpgradeModal && (
        <div className="fixed inset-0 z-[99] bg-black/60 flex items-center justify-center p-4" data-testid="team-upgrade-modal">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-5 max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between mb-3">
              <h3 className="text-base font-bold text-slate-900">Sem vaga para novos usuários</h3>
              <button onClick={() => setShowUpgradeModal(false)} className="p-1 hover:bg-slate-100 rounded" data-testid="close-upgrade-modal">
                <X className="w-4 h-4" />
              </button>
            </div>
            <UpgradeNotice planLabel={planLabel} limit={team?.limit} currentPlan={team?.plan} />
          </div>
        </div>
      )}

      {/* RESET PASSWORD MODAL */}
      {resetTarget && (
        <div className="fixed inset-0 z-[99] bg-black/60 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <p className="text-xs font-bold text-blue-600 uppercase tracking-wider mb-1">Redefinir senha</p>
                <h3 className="text-base font-bold text-slate-900">{resetTarget.name}</h3>
                <p className="text-xs text-slate-500">{resetTarget.email}</p>
              </div>
              <button onClick={() => setResetTarget(null)} className="p-1 hover:bg-slate-100 rounded">
                <X className="w-4 h-4" />
              </button>
            </div>
            <Label className="text-xs">Nova senha (mín. 6)</Label>
            <Input type="text" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Digite a nova senha" className="mt-1" data-testid="new-password-input" />
            <div className="flex gap-2 mt-4">
              <Button variant="outline" onClick={() => setResetTarget(null)} className="flex-1">Cancelar</Button>
              <Button onClick={handleResetPassword} className="flex-1 bg-blue-600 hover:bg-blue-700 text-white" data-testid="confirm-reset-password">Confirmar</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsTeamTab;
